import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import type { Message } from '@/components/chat/chat-types';
import type { UniversalChatContextPayload } from '@/components/chat/universal-chat-context';
import { DEFAULT_SUMMARY_LENGTH_INDEX, type ModelOption, SUMMARY_LENGTH_OPTIONS } from '@/lib/link-preview/summary-options';
import { toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import type { TweetMetrics, Tweet as TweetType } from '@/types/tweet';
import type { StreamState, SummaryContextSnapshot } from '../types';
import { createSummaryContextSections } from '../utils';
export interface UseSummaryChatParameters {
  appendMessages: (messages: Message[]) => void;
  buildPrompt: (custom: string, includeImages: boolean) => string;
  customPrompt: string;
  includeImagesInPrompt: boolean;
  isChatEnabled: boolean;
  metrics: TweetMetrics;
  openChat: () => void;
  registerContext: (context: UniversalChatContextPayload) => void;
  setSummaryContextSnapshot: (snapshot: SummaryContextSnapshot | null) => void;
  summaryContextSnapshot: SummaryContextSnapshot | null;
  summaryLengthIndex: number;
  summaryModel: ModelOption;
  summarySearchEnabled: boolean;
  summaryState: StreamState;
  tweet: TweetType;
}
export interface UseSummaryChatResult {
  chatFromSummary: () => void;
}
export function useSummaryChat(t0) {
  const $ = _c(18);
  const {
    tweet,
    metrics,
    summaryState,
    isChatEnabled,
    includeImagesInPrompt,
    customPrompt,
    summaryLengthIndex,
    summaryModel,
    summarySearchEnabled,
    openChat,
    appendMessages,
    registerContext,
    buildPrompt,
    summaryContextSnapshot,
    setSummaryContextSnapshot
  } = t0;
  let t1;
  if ($[0] !== appendMessages || $[1] !== buildPrompt || $[2] !== customPrompt || $[3] !== includeImagesInPrompt || $[4] !== isChatEnabled || $[5] !== metrics || $[6] !== openChat || $[7] !== registerContext || $[8] !== setSummaryContextSnapshot || $[9] !== summaryContextSnapshot || $[10] !== summaryLengthIndex || $[11] !== summaryModel || $[12] !== summarySearchEnabled || $[13] !== summaryState.text || $[14] !== tweet) {
    t1 = () => {
      const summaryText = toTrimmedNonEmptyString(summaryState.text);
      if (!(isChatEnabled && summaryText)) {
        return;
      }
      const progressMessageId = `tweet-summary-progress-${tweet.id}-${Date.now()}`;
      const progressTimestamp = new Date();
      openChat();
      appendMessages([{
        id: progressMessageId,
        role: "assistant",
        content: "Preparing tweet context\u2026",
        timestamp: progressTimestamp,
        status: "complete"
      }]);
      const fallbackSummaryLength = SUMMARY_LENGTH_OPTIONS[summaryLengthIndex]?.value ?? SUMMARY_LENGTH_OPTIONS[DEFAULT_SUMMARY_LENGTH_INDEX].value;
      const normalizedCustomPrompt = toTrimmedNonEmptyString(customPrompt) ?? "";
      const snapshot = summaryContextSnapshot ?? {
        prompt: buildPrompt(normalizedCustomPrompt, includeImagesInPrompt),
        includeImages: includeImagesInPrompt,
        customPrompt: normalizedCustomPrompt.length > 0 ? normalizedCustomPrompt : null,
        summaryLength: fallbackSummaryLength,
        model: summaryModel,
        searchMode: summarySearchEnabled ? "auto" : "off"
      };
      if (!summaryContextSnapshot) {
        setSummaryContextSnapshot(snapshot);
      }
      const metadata = {
        tweet: {
          id: tweet.id,
          text: tweet.text,
          author: tweet.author,
          createdAt: tweet.createdAt,
          metrics,
          entities: tweet.entities ?? null,
          mediaItems: tweet.mediaItems
        },
        summary: {
          text: summaryText,
          model: snapshot.model,
          length: snapshot.summaryLength,
          searchMode: snapshot.searchMode,
          includeImagesInPrompt: snapshot.includeImages,
          customPrompt: snapshot.customPrompt
        }
      } as const;
      const context = {
        source: `tweet-summary-${tweet.id}`,
        title: `Summary for tweet ${tweet.id}`,
        data: createSummaryContextSections({
          snapshot,
          metadata
        }),
        summary: summaryText
      };
      registerContext(context);
      appendMessages([{
        id: `${tweet.id}-summary-context-progress-${Date.now()}`,
        role: "assistant",
        content: "Tweet context ready. Generating ideas\u2026",
        timestamp: new Date(),
        status: "complete"
      }]);
      const instructionMessage = {
        id: `tweet-summary-example-instructions-${tweet.id}-${Date.now()}`,
        role: "system",
        content: ["You are helping a user explore the tweet summary context above.", "Provide 2-3 concise example questions the user could ask to investigate this tweet or its implications.", "Only list the questions as bullet points and do not answer them."].join("\n"),
        timestamp: new Date(),
        visibility: "hidden"
      } as const;
      const exampleRequestMessage = {
        id: `tweet-summary-examples-${tweet.id}-${Date.now()}`,
        role: "user",
        content: "Suggest a few example questions I could ask about this tweet.",
        timestamp: new Date(),
        visibility: "hidden"
      } as const;
      appendMessages([instructionMessage, exampleRequestMessage]);
    };
    $[0] = appendMessages;
    $[1] = buildPrompt;
    $[2] = customPrompt;
    $[3] = includeImagesInPrompt;
    $[4] = isChatEnabled;
    $[5] = metrics;
    $[6] = openChat;
    $[7] = registerContext;
    $[8] = setSummaryContextSnapshot;
    $[9] = summaryContextSnapshot;
    $[10] = summaryLengthIndex;
    $[11] = summaryModel;
    $[12] = summarySearchEnabled;
    $[13] = summaryState.text;
    $[14] = tweet;
    $[15] = t1;
  } else {
    t1 = $[15];
  }
  const chatFromSummary = t1;
  let t2;
  if ($[16] !== chatFromSummary) {
    t2 = {
      chatFromSummary
    };
    $[16] = chatFromSummary;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  return t2;
}